import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Rocket } from "lucide-react";
import { Button } from "@tubo/ui/components/ui/button";
import { Pencil, Network, List, Trash2, MoreHorizontal, History, X, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tubo/ui/components/ui/dropdown-menu";
import {
  useGetById,
  usePrecheck,
  useSubmitPublication,
  useListModelVersions,
  useGetModelVersionDetail,
  getListModelVersionsQueryKey,
  getGetByIdQueryKey,
  getModelReferencesQueryKey,
  getSemanticDataModelListQueryKey,
} from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import type { ModelValidationIssueVO } from "@tubo/api-models/hooks/semantic-data-model-api.schemas";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import { ErCanvas } from "@client/components/semantic-platform/er-canvas";
import { DetailHeaderBar } from "@client/components/common/detail-header";
import { ModelCommentDrawer } from "@client/components/semantic-platform/model-comment-drawer";
import { ViewModeSegment } from "@client/components/semantic-platform/view-mode-segment";
import {
  EntityDetailPanel,
  type EntityDetailSelection,
} from "@client/components/semantic-platform/data-models/entity-detail-panel";
import { ModelDetailTabs } from "@client/components/semantic-platform/data-models/model-detail/model-detail-tabs";
// validateSearch 在路由壳中执行，isModelDetailTab 必须直连无副作用小模块，
// 经组件模块导入会把 entity-editor/dagre 等重型依赖静态拖进入口 chunk。
import { isModelDetailTab } from "@client/components/semantic-platform/data-models/model-detail/model-detail-tab";
import type { ModelDetailTab } from "@client/components/semantic-platform/data-models/model-detail/model-detail-tab";
import { DeleteModelDialog } from "@client/components/semantic-platform/data-models/delete-model-dialog";
import { ModelExportButton } from "@client/components/semantic-platform/data-models/model-export-button";
import { SemanticDataModelCollaboratorDialog } from "@client/components/semantic-platform/resource-collaborator-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";

/** 版本切换下拉的历史版本状态文案(未发布/未提交的快照统称「已保存」) */
const VERSION_STATUS_LABEL: Record<string, string> = {
  published: "已发布",
  pendingReview: "待审批",
  deprecated: "已废弃",
};

/** 版本切换下拉(最新工作区 + 历史快照) */
function VersionSwitcher({
  versions,
  value,
  onChange,
  currentVersion,
}: {
  versions: Array<{ id: string; version: string; publishStatus?: string }>;
  value: string | null;
  onChange: (versionId: string | null) => void;
  currentVersion?: string;
}) {
  const workspaceLabel = currentVersion ? `工作区 · ${currentVersion}` : "最新工作区";
  if (versions.length === 0) {
    return currentVersion ? <span className="shrink-0 text-xs text-muted-foreground">{workspaceLabel}</span> : null;
  }
  const labelOf = (v: { version: string; publishStatus?: string }): string =>
    `${v.version} · ${VERSION_STATUS_LABEL[v.publishStatus ?? ""] ?? "已保存"}`;
  return (
    <Select
      items={[{ value: "__live__", label: workspaceLabel }, ...versions.map((v) => ({ value: v.id, label: labelOf(v) }))]}
      value={value ?? "__live__"}
      onValueChange={(v) => onChange(v && v !== "__live__" ? v : null)}
    >
      <SelectTrigger
        className="h-7 w-fit min-w-0 gap-1 border-border/60 bg-muted/20 px-2 text-xs text-muted-foreground shadow-none hover:bg-muted/50 hover:text-foreground"
        aria-label="切换工作区或历史版本"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__live__">{workspaceLabel}</SelectItem>
        {versions.map((v) => (
          <SelectItem key={v.id} value={v.id}>{labelOf(v)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** 历史版本查看提示条(只读) */
function VersionBanner({ version, onBack }: { version: { version: string; frozenAt: string }; onBack: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b bg-primary/5 px-4 py-1.5 text-xs">
      <History className="size-3.5 text-primary" />
      <span>
        正在查看历史版本 <span className="font-mono font-medium">{version.version}</span>
        （{new Date(version.frozenAt).toLocaleString()} 快照 · 只读）
      </span>
      <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={onBack}>
        <X className="mr-1 size-3" /> 返回最新
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/_app/semantic-platform/data-models/$id")({
  // 详情模式 Tab 深链接(?tab=relations 等);条件赋值保持可选参数类型
  validateSearch: (search: Record<string, unknown>): { ns?: string; tab?: ModelDetailTab } => ({
    ...(typeof search.ns === "string" && search.ns ? { ns: search.ns } : {}),
    ...(isModelDetailTab(search.tab) ? { tab: search.tab } : {}),
  }),
  component: DataModelDetailPage,
});

function DataModelDetailPage() {
  const { id } = Route.useParams();
  const { ns, tab } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"canvas" | "detail">("canvas");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [entitySel, setEntitySel] = useState<EntityDetailSelection | null>(null);
  const [publishConfirmIssues, setPublishConfirmIssues] = useState<ModelValidationIssueVO[] | null>(null);
  const [collaboratorOpen, setCollaboratorOpen] = useState(false);
  // 版本切换:null=最新工作区;选中历史版本后整页只读渲染该快照
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const { data: model, isLoading } = useGetById(id);
  const { data: versions } = useListModelVersions(id);
  const { data: versionDetail, isLoading: versionLoading } = useGetModelVersionDetail(id, viewingVersionId ?? "", {
    query: { enabled: viewingVersionId !== null },
  });
  const viewingVersion = viewingVersionId !== null ? versions?.find((v) => v.id === viewingVersionId) : undefined;



  // 提交发布(发布即快照):precheck 校验 → warning 确认 → 对已保存工作区打快照进审批
  const precheck = usePrecheck(id, { query: { enabled: false } });
  const submitMutation = useSubmitPublication({
    mutation: {
      onSuccess: (res) => {
        setPublishConfirmIssues(null);
        queryClient.invalidateQueries({ queryKey: getGetByIdQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getSemanticDataModelListQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListModelVersionsQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getModelReferencesQueryKey(id) });
        toast.success(
          res.publicationStatus === "approved"
            ? `模型已发布(publication ${res.publicationId})`
            : `已提交发布审批(publication ${res.publicationId}),新版本进入待审批`,
        );
      },
      onError: () => toast.error("提交失败(存在错误级校验问题或版本已在发布流中)"),
    },
  });
  const handlePublish = () => {
    precheck
      .refetch()
      .then((res) => {
        const issues = res.data ?? [];
        if (issues.some((i) => i.severity === "error")) {
          toast.error("存在错误级校验问题,请进入编辑器「校验」查看并修复");
          return;
        }
        const warnings = issues.filter((i) => i.severity === "warning");
        if (warnings.length > 0) {
          setPublishConfirmIssues(warnings);
          return;
        }
        submitMutation.mutate({ id, data: {} });
      })
      .catch(() => toast.error("校验失败,无法提交发布"));
  };

  if (isLoading || !model) return <div className="p-4 text-muted-foreground">加载中…</div>;

  // 内容源:查看历史版本时用快照(未加载完成前由加载态兜底),否则用工作区
  const displayed = viewingVersionId !== null && versionDetail
    ? {
        ...model,
        entities: versionDetail.entities,
        relationships: versionDetail.relationships,
        constraints: versionDetail.constraints,
        views: versionDetail.views,
        graphProjections: versionDetail.graphProjections,
        canvasLayout: versionDetail.canvasLayout,
      }
    : model;

  // 全屏沉浸形态(域布局对模型详情隐藏二级目录),页内自渲染 header:
  // 返回 │ 数据模型 > 模型名+状态+工作区/版本,中间视图切换,右动作
  const headerBar = (
    <DetailHeaderBar
      moduleName="数据模型"
      backLabel="返回数据模型列表"
      onBack={() => navigate({ to: "/semantic-platform/data-models", search: ns ? { ns } : {} })}
      onModuleClick={() => navigate({ to: "/semantic-platform/data-models", search: ns ? { ns } : {} })}
      center={
        <ViewModeSegment
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: "canvas", label: "画布", icon: Network },
            { value: "detail", label: "详情", icon: List },
          ]}
        />
      }
      centerOnViewport
      actions={viewingVersionId === null ? (
        <>
          <div className="mx-1 h-4 w-px bg-border" />
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => navigate({ to: "/semantic-platform/data-models/edit/$id", params: { id }, search: ns ? { ns } : {} })}
          >
            <Pencil className="mr-1 size-3" /> 编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={submitMutation.isPending}
            onClick={handlePublish}
            title="对已保存内容打版本快照并进入发布审批"
          >
            <Rocket className="mr-1 size-3" /> 提交发布
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setCollaboratorOpen(true)}
            title="管理模型协作者"
          >
            <Users className="mr-1 size-3" /> 协作者
          </Button>
          <div className="mx-1 h-4 w-px bg-border" />
          <ModelCommentDrawer modelId={id} compact triggerClassName="size-7" />
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="ghost" size="icon-sm" className="size-7" aria-label="更多操作" title="更多操作">
                  <MoreHorizontal className="size-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              <ModelExportButton modelId={id} modelCode={model.code} />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                title="删除模型(进回收站,可恢复;有派生或引用时不可删)"
              >
                <Trash2 /> 删除模型
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : null}
    >
      <span className="min-w-0 truncate text-sm font-semibold" title={model.name}>{model.name}</span>
      <VersionSwitcher
        versions={versions ?? []}
        value={viewingVersionId}
        onChange={setViewingVersionId}
        currentVersion={model.currentVersion}
      />
    </DetailHeaderBar>
  );

  const versionLoadingHere = viewingVersionId !== null && (versionLoading || !versionDetail);

  return (
    <div className="flex h-full flex-col">
      {headerBar}
      {viewingVersionId !== null && viewingVersion && (
        <VersionBanner version={viewingVersion} onBack={() => setViewingVersionId(null)} />
      )}
      {versionLoadingHere ? (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">版本加载中…</div>
      ) : viewMode === "canvas" ? (
        /* 画布模式:整页纯净画布,实体定义/版本历史等内容归「详情」模式;
           实体/属性详情以同层停靠侧栏呈现(非浮层,不影响画布交互) */
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1">
            {(displayed.entities?.length ?? 0) > 0 ? (
              <ErCanvas
                entities={displayed.entities ?? []}
                relationships={displayed.relationships ?? []}
                canvasLayout={displayed.canvasLayout}
                onEntityClick={(key) => {
                  const e = (displayed.entities ?? []).find((x) => (x.id ?? x.code) === key);
                  if (e) setEntitySel({ entityCode: e.code });
                }}
                onAttributeClick={(key, attrCode) => {
                  const e = (displayed.entities ?? []).find((x) => (x.id ?? x.code) === key);
                  if (e) setEntitySel({ entityCode: e.code, attrCode });
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                暂无实体，画布无可视内容
              </div>
            )}
          </div>
          {entitySel && (
            <EntityDetailPanel
              entities={model.entities ?? []}
              selection={entitySel}
              onSelectionChange={setEntitySel}
            />
          )}
        </div>
      ) : (
        /* 详情模式:页级 Tab(实体/关系/约束/质量中心/版本历史[/派生管理]);
           实体 Tab 内左目录右字段;字段行点击与画布点击共用停靠侧栏钻取属性详情 */
        <div className="flex min-h-0 flex-1 flex-col">
          {displayed.desc && (
            <p className="shrink-0 border-b px-4 py-2 text-sm text-muted-foreground">{displayed.desc}</p>
          )}
          <div className="flex min-h-0 flex-1">
            <ModelDetailTabs
              model={displayed}
              tab={tab ?? "entities"}
              namespaceId={ns}
              onTabChange={(t) =>
                navigate({
                  to: ".",
                  search: ns ? { ns, tab: t } : { tab: t },
                  replace: true,
                })
              }
              onSelectAttribute={(entityCode, attrCode) => setEntitySel({ entityCode, attrCode })}
              viewingVersionId={viewingVersionId ?? undefined}
              onViewVersion={(versionId) => setViewingVersionId(versionId)}
            />
            {entitySel && (
              <EntityDetailPanel
                entities={displayed.entities ?? []}
                selection={entitySel}
                onSelectionChange={setEntitySel}
              />
            )}
          </div>
        </div>
      )}

      {/* 提交发布风险确认:warning 级问题须显式确认 */}
      <AlertDialog open={publishConfirmIssues !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>存在需要确认的风险项</AlertDialogTitle>
            <AlertDialogDescription>
              {(publishConfirmIssues ?? []).map((i) => i.message).join("；")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPublishConfirmIssues(null)}>返回处理</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submitMutation.mutate({ id, data: { confirmedRiskCodes: (publishConfirmIssues ?? []).map((i) => i.code) } })}
            >
              确认风险并提交
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteModelDialog open={deleteOpen} onOpenChange={setDeleteOpen} id={id} name={model.name} />
      <SemanticDataModelCollaboratorDialog
        open={collaboratorOpen}
        onOpenChange={setCollaboratorOpen}
        modelId={id}
        modelName={model.name}
      />
    </div>
  );
}
